import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  FormGroup,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Alert,
  Progress,
} from "reactstrap";
import axios from "axios";
import swal from "sweetalert";
import { DATA_DISABILITAS_GROUP } from "../utils";
import { logoutUser } from "../actions/AuthAction";

function InputIndikator(props) {
  const [dataDisabilitas, setDataDisabilitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState("kecamatan"); // "kecamatan" atau "desa"
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedJenisDisabilitas, setSelectedJenisDisabilitas] = useState("");
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);

  // Jenis disabilitas yang tersedia
  const jenisDisabilitasOptions = [
    "DISABILITAS NETRA/BUTA",
    "DISABILITAS MENTAL/JIWA",
    "DISABILITAS FISIK",
    "DISABILITAS FISIK DAN MENTAL",
    "DISABILITAS RUNGU/WICARA",
    "DISABILITAS LAINNYA",
  ];

  const fetchDataDisabilitas = useCallback(async () => {
    try {
      setLoading(true);
      let endpoint = "";

      if (viewType === "kecamatan") {
        endpoint = `${DATA_DISABILITAS_GROUP}kecamatan-detail`;
      } else {
        endpoint = `${DATA_DISABILITAS_GROUP}kecamatan-detail`;
      }

      const response = await axios.get(endpoint);
      if (response.data.success) {
        setDataDisabilitas(response.data.data);
      } else {
        swal("Gagal", "Gagal mengambil data disabilitas", "error");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      swal("Gagal", "Gagal mengambil data disabilitas", "error");
    } finally {
      setLoading(false);
    }
  }, [viewType]);

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      // User data is set but not used in this component
    } else {
      logoutUser(props.history);
    }
    fetchDataDisabilitas();
  }, [props.history, fetchDataDisabilitas]);

  const filteredData = dataDisabilitas.filter((item) => {
    const matchesSearch =
      (viewType === "kecamatan"
        ? item.kecamatan?.toLowerCase().includes(searchTerm.toLowerCase())
        : item.desa?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (viewType === "desa" &&
        item.kecamatan?.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesJenisDisabilitas =
      !selectedJenisDisabilitas ||
      item.jenis_disabilitas_list?.includes(selectedJenisDisabilitas);

    return matchesSearch && matchesJenisDisabilitas;
  });

  const formatNumber = (num) => {
    return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "0";
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return "success";
    if (percentage >= 60) return "warning";
    return "danger";
  };

  const calculateTotalPercentage = (item) => {
    const total = item.total_disabilitas || 0;
    const sudahRekam = item.sudah_rekam || 0;
    return total > 0 ? Math.round((sudahRekam / total) * 100) : 0;
  };

  const openDetailModal = (data) => {
    setSelectedData(data);
    setShowDetailModal(true);
  };

  const getJenisDisabilitasList = (jenisDisabilitasList) => {
    if (!jenisDisabilitasList) return [];
    return jenisDisabilitasList.split(",").map((item) => item.trim());
  };

  return (
    <>
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <CardTitle tag="h4">
                  Data Jenis Disabilitas per{" "}
                  {viewType === "kecamatan" ? "Kecamatan" : "Desa"}
                </CardTitle>

                {/* Radio Button untuk memilih tampilan */}
                <Row className="mt-3">
                  <Col md="4">
                    <FormGroup tag="fieldset">
                      <legend className="col-form-label col-sm-2">
                        Tampilan Data:
                      </legend>
                      <Col sm={10}>
                        <FormGroup check>
                          <Label check>
                            <Input
                              type="radio"
                              name="viewType"
                              value="kecamatan"
                              checked={viewType === "kecamatan"}
                              onChange={(e) => setViewType(e.target.value)}
                            />{" "}
                            Per Kecamatan
                          </Label>
                        </FormGroup>
                        <FormGroup check>
                          <Label check>
                            <Input
                              type="radio"
                              name="viewType"
                              value="desa"
                              checked={viewType === "desa"}
                              onChange={(e) => setViewType(e.target.value)}
                            />{" "}
                            Per Desa
                          </Label>
                        </FormGroup>
                      </Col>
                    </FormGroup>
                  </Col>

                  {/* Filter Jenis Disabilitas */}
                  <Col md="4">
                    <FormGroup>
                      <Label>Filter Jenis Disabilitas</Label>
                      <Input
                        type="select"
                        value={selectedJenisDisabilitas}
                        onChange={(e) =>
                          setSelectedJenisDisabilitas(e.target.value)
                        }
                      >
                        <option value="">Semua Jenis Disabilitas</option>
                        {jenisDisabilitasOptions.map((jenis) => (
                          <option key={jenis} value={jenis}>
                            {jenis}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>

                  {/* Search Bar */}
                  <Col md="4">
                    <FormGroup>
                      <Label>Cari</Label>
                      <InputGroup>
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="tim-icons icon-zoom-split-in"></i>
                          </InputGroupText>
                        </InputGroupAddon>
                        <Input
                          placeholder={`Cari berdasarkan ${
                            viewType === "kecamatan" ? "kecamatan" : "desa"
                          }...`}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </InputGroup>
                    </FormGroup>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                {loading ? (
                  <div className="text-center">
                    <div className="spinner-border" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <Table className="tablesorter" responsive>
                      <thead className="text-primary">
                        <tr>
                          <th style={{ minWidth: "50px" }}>No</th>
                          {viewType === "kecamatan" ? (
                            <th style={{ minWidth: "200px" }}>Kecamatan</th>
                          ) : (
                            <>
                              <th style={{ minWidth: "200px" }}>Desa</th>
                              <th style={{ minWidth: "200px" }}>Kecamatan</th>
                            </>
                          )}
                          <th style={{ minWidth: "120px" }}>
                            Total Disabilitas
                          </th>
                          <th style={{ minWidth: "100px" }}>Laki-laki</th>
                          <th style={{ minWidth: "100px" }}>Perempuan</th>
                          <th style={{ minWidth: "120px" }}>Sudah Rekam</th>
                          <th style={{ minWidth: "120px" }}>Belum Rekam</th>
                          <th style={{ minWidth: "150px" }}>Progress Rekam</th>
                          <th style={{ minWidth: "200px" }}>
                            Jenis Disabilitas
                          </th>
                          <th style={{ minWidth: "100px" }}>Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.map((item, index) => {
                          const progressPercentage =
                            calculateTotalPercentage(item);
                          const jenisDisabilitasList = getJenisDisabilitasList(
                            item.jenis_disabilitas_list
                          );
                          return (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              {viewType === "kecamatan" ? (
                                <td style={{ fontWeight: "bold" }}>
                                  {item.kecamatan || "-"}
                                </td>
                              ) : (
                                <>
                                  <td style={{ fontWeight: "bold" }}>
                                    {item.desa || "-"}
                                  </td>
                                  <td>{item.kecamatan || "-"}</td>
                                </>
                              )}
                              <td
                                style={{ fontWeight: "bold", color: "#007bff" }}
                              >
                                {formatNumber(item.total_disabilitas)}
                              </td>
                              <td>{formatNumber(item.laki_laki)}</td>
                              <td>{formatNumber(item.perempuan)}</td>
                              <td>
                                <span className="badge badge-success">
                                  {formatNumber(item.sudah_rekam)}
                                </span>
                              </td>
                              <td>
                                <span className="badge badge-warning">
                                  {formatNumber(item.belum_rekam)}
                                </span>
                              </td>
                              <td style={{ width: "30%" }}>
                                <Progress
                                  value={progressPercentage}
                                  color={getProgressColor(progressPercentage)}
                                >
                                  {progressPercentage}%
                                </Progress>
                              </td>
                              <td>
                                <div style={{ maxWidth: "200px" }}>
                                  {jenisDisabilitasList.length > 0 ? (
                                    jenisDisabilitasList
                                      .slice(0, 2)
                                      .map((jenis, idx) => (
                                        <span
                                          key={idx}
                                          className="badge badge-info mr-1 mb-1"
                                        >
                                          {jenis}
                                        </span>
                                      ))
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                  {jenisDisabilitasList.length > 2 && (
                                    <span className="badge badge-secondary">
                                      +{jenisDisabilitasList.length - 2} lagi
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => openDetailModal(item)}
                                >
                                  <i className="tim-icons icon-zoom-split-in"></i>
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>

                    {!loading && filteredData.length === 0 && (
                      <Alert color="info">
                        {searchTerm || selectedJenisDisabilitas
                          ? `Tidak ada data yang sesuai dengan filter`
                          : `Belum ada data disabilitas per ${
                              viewType === "kecamatan" ? "kecamatan" : "desa"
                            }`}
                      </Alert>
                    )}

                    {!loading && filteredData.length > 0 && (
                      <div className="mt-3">
                        <small className="text-muted">
                          Menampilkan {filteredData.length} dari{" "}
                          {dataDisabilitas.length} data{" "}
                          {viewType === "kecamatan" ? "kecamatan" : "desa"}
                          {searchTerm && ` (hasil pencarian: "${searchTerm}")`}
                          {selectedJenisDisabilitas &&
                            ` (filter: ${selectedJenisDisabilitas})`}
                        </small>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Modal Detail */}
        <Modal
          isOpen={showDetailModal}
          toggle={() => setShowDetailModal(false)}
          size="lg"
        >
          <ModalHeader toggle={() => setShowDetailModal(false)}>
            Detail Jenis Disabilitas
          </ModalHeader>
          <ModalBody>
            {selectedData && (
              <div>
                <Row>
                  <Col md="6">
                    <h6>Informasi Lokasi</h6>
                    <p>
                      <strong>Kecamatan:</strong> {selectedData.kecamatan}
                    </p>
                    <p>
                      <strong>Desa:</strong> {selectedData.desa}
                    </p>
                  </Col>
                  <Col md="6">
                    <h6>Statistik</h6>
                    <p>
                      <strong>Total Disabilitas:</strong>{" "}
                      {formatNumber(selectedData.total_disabilitas)}
                    </p>
                    <p>
                      <strong>Laki-laki:</strong>{" "}
                      {formatNumber(selectedData.laki_laki)}
                    </p>
                    <p>
                      <strong>Perempuan:</strong>{" "}
                      {formatNumber(selectedData.perempuan)}
                    </p>
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md="12">
                    <h6>Status Rekam</h6>
                    <p>
                      <strong>Sudah Rekam:</strong>{" "}
                      {formatNumber(selectedData.sudah_rekam)}
                    </p>
                    <p>
                      <strong>Belum Rekam:</strong>{" "}
                      {formatNumber(selectedData.belum_rekam)}
                    </p>
                    <Progress
                      value={calculateTotalPercentage(selectedData)}
                      color={getProgressColor(
                        calculateTotalPercentage(selectedData)
                      )}
                      className="mt-2"
                    >
                      {calculateTotalPercentage(selectedData)}%
                    </Progress>
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md="12">
                    <h6>Jenis Disabilitas yang Ada</h6>
                    <div>
                      {getJenisDisabilitasList(
                        selectedData.jenis_disabilitas_list
                      ).map((jenis, idx) => (
                        <span key={idx} className="badge badge-info mr-2 mb-2">
                          {jenis}
                        </span>
                      ))}
                    </div>
                  </Col>
                </Row>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowDetailModal(false)}>
              Tutup
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </>
  );
}

export default InputIndikator;
